import { SettingKey, SettingsResponseDTO } from "@dto/settings.dto";
import { ChangePasswordDTO } from "@dto/usersResponse.dto";
import { Form, Input } from 'antd';
import { AxiosResponse } from "axios";
import { FC, useEffect, useState } from "react";
import { PasswordUtil } from "src/common/util/PasswordUtil";
import { XmInputPassword } from "src/component/inputPassword/XmInputPassword";
import { XmModal } from "src/component/modal/XmModal";
import { BasicFormProps } from "src/data/props";
import { Log } from "src/service/log.service";
import { getApi, putApi } from "src/util/apiCalls";
import { PasswordUtil as PasswordUtilUI } from "src/util/PasswordUtil";
import "./Login.less";

export interface ChangePasswordProps {
  timestamp: number;
  initialUsername: string;
}

export const ChangeExpiredPasswordModal: FC<ChangePasswordProps> = ({ timestamp = 0, initialUsername }) => {
  const [passwordForm] = Form.useForm<ChangePasswordDTO>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [passwordRules, setPasswordRules] = useState<Record<string, string | number | boolean>>({});
  const [open, setOpen] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>("");
  useEffect(() => {
    if (timestamp) {
      passwordForm.resetFields();
      if (initialUsername) {
        passwordForm.setFieldsValue({ username: initialUsername });
      } else {
        passwordForm.setFieldsValue({ username: undefined });
      }
      getApi<SettingsResponseDTO>('/api/password-policy', null).then(
        (response: AxiosResponse<SettingsResponseDTO>) => {
          const defaults: Record<string, string | number | boolean> = PasswordUtil.getDefaults();
          const settings: Record<string, string | number | boolean> = response.data.data;
          SettingKey.PASSWORD.reType(settings);
          for (const setting in defaults) {
            settings[setting] = settings[setting] ?? defaults[setting];
          }
          setPasswordRules(settings);
        },
        (reason: unknown) => {
          Log.error('Failed to get password policy!', reason as Error);
        }
      );
      setOpen(true);
      setErrorMsg("");
    }
  }, [timestamp, initialUsername, passwordForm]);
  const handleSubmit = async (values: ChangePasswordDTO & { confirmPassword?: string; }): Promise<void> => {
    const timer: number = window.setTimeout((): void => {
      setIsSubmitting(true);
    }, 500);
    setErrorMsg("");
    try {
      const data: ChangePasswordDTO = {
        username: values.username,
        oldPassword: values.oldPassword,
        password: values.password
      };
      await putApi('/api/users/v1/change-password', data);
      clearTimeout(timer);
      setIsSubmitting(false);
      Log.success('Password changed successfully. Please log in with your new password.');
      setOpen(false);
    } catch (error: unknown) {
      clearTimeout(timer);
      setIsSubmitting(false);
      const msg: string = (error as { response?: { data?: { message?: string; }; }; })?.response?.data?.message || 'Failed to change password. Please check your credentials.';
      setErrorMsg(msg);
    }
  };
  const onCancel = (): void => {
    setOpen(false);
  };
  return (
    <XmModal open={open} onCancel={onCancel} okText="Change Password" onOk={() => passwordForm.submit()} title="Change password" confirmLoading={isSubmitting} destroyOnHidden={true}>
      <div className="xm-modal-change-password-expired">
        Your password has expired. Please change it.
      </div>
      <Form form={passwordForm} preserve={false} id="changeExpiredPasswordForm" className="xm-form-validation" {...BasicFormProps} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}
        onFinish={(values: unknown): void => { void handleSubmit(values as ChangePasswordDTO & { confirmPassword?: string; }); }}>
        <Form.Item
          name="username"
          label="Username"
          rules={[
            { required: true, whitespace: true, message: 'The Username field is required.' }
          ]}
        >
          <Input placeholder="Username" autoComplete="username" disabled={isSubmitting} />
        </Form.Item>
        <Form.Item
          name="oldPassword"
          label="Old Password"
          rules={[
            { required: true, whitespace: true, message: 'The Old Password field is required.' }
          ]}
        >
          <Input.Password placeholder="Old Password" autoComplete="current-password" disabled={isSubmitting} />
        </Form.Item>
        <Form.Item
          name="password"
          label="New Password"
          dependencies={["oldPassword"]}
          rules={[
            { required: true, whitespace: true, message: 'The New Password field is required.' },
            ({ getFieldValue }) => ({
              // Check if new password is different from old password
              validator: (_: unknown, value: string): Promise<void> => (!value || value !== (getFieldValue("oldPassword") as string))
                ? Promise.resolve()
                : Promise.reject(new Error('New password must be different from current password'))
            }),
            ({ getFieldValue }) => ({
              // Check password strength; message can be a ReactNode for detailed formatting
              validator: (_: unknown, value: string): Promise<void> => (!value || PasswordUtil.isStrong(value, passwordRules))
                ? Promise.resolve()
                : Promise.reject(new Error()),
              message: PasswordUtilUI.getPasswordValidationMessage((getFieldValue("password") as string) || "", passwordRules)
            })
          ]}
          hasFeedback
        >
          <XmInputPassword placeholder="New Password" disabled={isSubmitting} />
        </Form.Item>
        <Form.Item
          name="confirmPassword"
          label="Confirm New Password"
          dependencies={["password"]}
          rules={[
            { required: true, whitespace: true, message: 'The Confirm Password field is required.' },
            ({ getFieldValue }) => ({
              validator: (_: unknown, value: string): Promise<void> => {
                if (!value || getFieldValue("password") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Passwords do not match'));
              },
            })
          ]}
          hasFeedback
        >
          <XmInputPassword placeholder="Confirm New Password" disabled={isSubmitting} />
        </Form.Item>
        <button type="submit" hidden />
      </Form>
      {errorMsg &&
        <div className="xm-modal-change-password-error">
          {errorMsg}
        </div>
      }
    </XmModal>
  );
};
